import React, { useCallback, useEffect, useState } from 'react';

import {
  $isListItemNode,
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $findMatchingParent, $getNearestNodeOfType } from '@lexical/utils';
import { $patchStyleText } from '@lexical/selection';
import { $getSelection, $isElementNode, $isRangeSelection, FORMAT_TEXT_COMMAND, LexicalEditor } from 'lexical';
import { FloatingMenuComponentProps } from './FloatingMenuPlugin';
import { TextFormatState, TextFormatType } from '../types';
import { $checkIfFullSelection, checkIfFullSelection, parseNodesFromSelection } from './selectionUtils';

import { Flex } from '@mutiny-pkg/dumpster-ui/components/Flex';
import { Icons } from '@mutiny-pkg/dumpster-ui/icons';
import { IconButton } from '@mutiny-pkg/dumpster-ui/components/Button';
import { Divider } from '@mutiny-pkg/dumpster-ui/components/Dividers';

import * as styles from './FloatingMenu.css';
import { $isLinkNode } from '@lexical/link';
import { FloatingLinkEditorPlugin } from '@mutiny-pkg/bin-ui/src/richtext/FloatingLinkMenuPlugin/FloatingLinkMenuPlugin';
import {
  $isExtendedTextNode,
  ExtendedTextNode,
} from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';

import { ColorMenu } from '@mutiny-pkg/bin-ui/src/richtext/FloatingMenuPlugin/ColorMenu/ColorMenu';
import { ColorMenuType } from '@mutiny-pkg/bin-ui/src/richtext/FloatingMenuPlugin/ColorMenu/types';
import { TextAlignButton } from '@mutiny-pkg/bin-ui/src/richtext/FloatingMenuPlugin/TextAlignButton';
import { FloatingMenuTooltip } from './FloatingMenuTooltip';
import RemoveFormattingIcon from '@mutiny-pkg/bin-ui/src/richtext/FloatingMenuPlugin/RemoveFormattingIcon';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  'custom-paragraph': 'Normal',
  quote: 'Quote',
  link: 'Link',
  'custom-link': 'Link',
};

type FloatingMenuState = {
  isBold: boolean;
  isCode: boolean;
  isItalic: boolean;
  isStrikethrough: boolean;
  isUnderline: boolean;
  hasFormatting: boolean;
  highlightColor: string;
  fontColor: string;
};

export function FloatingMenu({ editor, options }: FloatingMenuComponentProps) {
  const [format, setFormat] = useState<FloatingMenuState>({
    isBold: false,
    isCode: false,
    isItalic: false,
    isStrikethrough: false,
    isUnderline: false,
    hasFormatting: false,
    highlightColor: '',
    fontColor: '',
  });

  // const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  // const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('custom-paragraph');
  const [openColorMenuType, setOpenColorMenuType] = useState<ColorMenuType | null>(null);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();

      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              return $isElementNode(e);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // setIsRTL($isParentElementRTL(selection));

      // Update links
      // const node = getSelectedNode(selection);
      // const parent = node.getParent();
      // if ($isLinkNode(parent) || $isLinkNode(node)) {
      //   setIsLink(true);
      // } else {
      //   setIsLink(false);
      // }

      // const tableNode = $findMatchingParent(node, $isTableNode);
      // if ($isTableNode(tableNode)) {
      //   setRootType('table');
      // } else {
      //   setRootType('root');
      // }

      if (elementDOM !== null) {
        // setSelectedElementKey(elementKey);

        if ($isListItemNode(element)) {
          const list = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)!;
          const type = list.getListType();
          setBlockType(type);
        } else if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else if ($isLinkNode(element)) {
          setBlockType('link');
        } else {
          // const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          const type = element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
          // if ($isCodeNode(element)) {
          //   const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
          //   setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : '');
          // }
        }
      }
      // Handle buttons
      // setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#000'));
      // setBgColor($getSelectionStyleValueForProperty(selection, 'background-color', '#fff'));
      // setFontFamily($getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'));
      // let matchingParent;
      // if ($isLinkNode(parent)) {
      //   // If node is a link, we need to fetch the parent paragraph node to set format
      //   matchingParent = $findMatchingParent(
      //     node,
      //     (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
      //   );
      // }

      // If matchingParent is a valid node, pass it's format type
      // setElementFormat(
      //   $isElementNode(matchingParent)
      //     ? matchingParent.getFormatType()
      //     : $isElementNode(node)
      //     ? node.getFormatType()
      //     : parent?.getFormatType() || 'left',
      // );
    }
    if ($isRangeSelection(selection)) {
      const isFullSelection = $checkIfFullSelection(selection);
      const fullSelectionTextFormatState = options?.fullSelectionTextFormatState;

      // Only indicate hasHighlight and hasFontColor if all of the nodes have the same value.
      const nodesToCheck = selection.getNodes().filter((node) => $isExtendedTextNode(node));

      // Check that any part of the text is highlighted or colored
      const hasFormatting =
        nodesToCheck.some((node) => {
          return (
            node.hasFormat('bold') ||
            node.hasFormat('italic') ||
            node.hasFormat('underline') ||
            node.hasFormat('strikethrough') ||
            node.getResolvedHighlightColor() ||
            node.getResolvedFontColor()
          );
        }) ||
        (isFullSelection && !!fullSelectionTextFormatState
          ? Object.values(fullSelectionTextFormatState).some((isActive) => isActive)
          : false);

      // Helper method to check that all values are the same truthy value
      const allSameValue = (fn: (node?: ExtendedTextNode) => string | undefined) => {
        return !!fn(nodesToCheck[0]) && nodesToCheck.every((node) => fn(node) === fn(nodesToCheck[0]));
      };
      const highlightColor = allSameValue((node) => node?.getResolvedHighlightColor())
        ? nodesToCheck[0]?.getResolvedHighlightColor() ?? ''
        : '';
      const fontColor = allSameValue((node) => node?.getResolvedFontColor())
        ? nodesToCheck[0]?.getResolvedFontColor() ?? ''
        : '';

      // Update text format
      setFormat({
        isBold:
          isFullSelection && fullSelectionTextFormatState
            ? fullSelectionTextFormatState.bold
            : selection.hasFormat('bold'),
        isCode: selection.hasFormat('code'),
        isItalic:
          isFullSelection && fullSelectionTextFormatState
            ? fullSelectionTextFormatState.italic
            : selection.hasFormat('italic'),
        isStrikethrough:
          isFullSelection && fullSelectionTextFormatState
            ? fullSelectionTextFormatState.strikethrough
            : selection.hasFormat('strikethrough'),
        isUnderline:
          isFullSelection && fullSelectionTextFormatState
            ? fullSelectionTextFormatState.underline
            : selection.hasFormat('underline'),
        highlightColor,
        fontColor,
        hasFormatting,
      });

      // setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '15px'));
    }
  }, [editor, options?.fullSelectionTextFormatState]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        $updateToolbar();
      });
    });
  }, [editor, $updateToolbar]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [editor, $updateToolbar, options?.fullSelectionTextFormatState]);

  const handleApplyTextColor = (color: string) => {
    if (options?.onColorChange) {
      const isFullSelection = checkIfFullSelection(editor);
      const shouldContinue = options.onColorChange(color, isFullSelection, 'text');

      if (shouldContinue === false) {
        return;
      }
    }

    colorText(editor, color);
  };

  const handleApplyHighlightColor = (color: string) => {
    if (options?.onColorChange) {
      const isFullSelection = checkIfFullSelection(editor);
      const shouldContinue = options.onColorChange(color, isFullSelection, 'highlight');

      if (shouldContinue === false) {
        return;
      }
    }

    highlightText(editor, color);
  };

  const textFormatState: TextFormatState = {
    bold: format.isBold,
    italic: format.isItalic,
    underline: format.isUnderline,
    strikethrough: format.isStrikethrough,
  };

  const handleApplyTextFormat = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, type: TextFormatType) => {
    event.preventDefault();
    if (options?.onTextFormatChange) {
      const isFullSelection = checkIfFullSelection(editor);
      const shouldContinue = options.onTextFormatChange(type, isFullSelection, textFormatState[type]);
      if (shouldContinue === false) {
        return;
      }
    }
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
  };

  return (
    <Flex.Container
      onMouseDown={(e) => e.preventDefault()}
      className={styles.container}
      gap="4u"
      paddingX="4u"
      paddingY="2u"
    >
      <FloatingMenuTooltip title="Color">
        <ColorMenu
          type={ColorMenuType.Text}
          onApplyColor={handleApplyTextColor}
          isButtonActive={!!format.fontColor}
          currentColor={format.fontColor}
          openColorMenuType={openColorMenuType}
          setOpenColorMenuType={setOpenColorMenuType}
          themeColors={options?.themeColors}
        />
      </FloatingMenuTooltip>
      <FloatingMenuTooltip title="Highlight">
        <ColorMenu
          type={ColorMenuType.Highlight}
          onApplyColor={handleApplyHighlightColor}
          isButtonActive={!!format.highlightColor}
          currentColor={format.highlightColor}
          openColorMenuType={openColorMenuType}
          setOpenColorMenuType={setOpenColorMenuType}
          themeColors={options?.themeColors}
        />
      </FloatingMenuTooltip>
      <Divider style={{ minHeight: 24 }} />
      <FloatingMenuTooltip title="Bold" keyboardShortcut="⌘+B">
        <IconButton
          tabIndex={-1}
          className={styles.toolbarButton({ active: format.isBold })}
          label="Bold"
          icon={Icons.Bold}
          variant="text"
          onMouseDown={(event) => handleApplyTextFormat(event, 'bold')}
        />
      </FloatingMenuTooltip>
      <FloatingMenuTooltip title="Underline" keyboardShortcut="⌘+U">
        <IconButton
          tabIndex={-1}
          className={styles.toolbarButton({ active: format.isUnderline })}
          label="Underline"
          icon={Icons.Underline}
          variant="text"
          onMouseDown={(event) => handleApplyTextFormat(event, 'underline')}
        />
      </FloatingMenuTooltip>
      <FloatingMenuTooltip title="Italic" keyboardShortcut="⌘+I">
        <IconButton
          tabIndex={-1}
          className={styles.toolbarButton({ active: format.isItalic })}
          label="Italic"
          icon={Icons.Italic}
          variant="text"
          onMouseDown={(event) => handleApplyTextFormat(event, 'italic')}
        />
      </FloatingMenuTooltip>
      <FloatingMenuTooltip title="Strikethrough" keyboardShortcut="⌘+⇧+X">
        <IconButton
          tabIndex={-1}
          className={styles.toolbarButton({ active: format.isStrikethrough })}
          label="Strikethrough"
          icon={Icons.Strikethrough}
          variant="text"
          onMouseDown={(event) => handleApplyTextFormat(event, 'strikethrough')}
        />
      </FloatingMenuTooltip>

      <FloatingMenuTooltip title="Remove formatting">
        <IconButton
          tabIndex={-1}
          className={styles.toolbarButton({ active: format.hasFormatting })}
          label="Remove formatting"
          icon={RemoveFormattingIcon}
          variant="text"
          onMouseDown={(event) => {
            event.preventDefault();
            removeFormatting(editor);
          }}
        />
      </FloatingMenuTooltip>

      {options?.enableExtendedTextFormatting && (
        <>
          <Divider style={{ minHeight: 24 }} />
          <FloatingMenuTooltip title="Bullets">
            <IconButton
              tabIndex={-1}
              className={styles.toolbarButton({ active: blockType === 'bullet' })}
              label="Unordered list"
              icon={Icons.UnorderedList}
              variant="text"
              onMouseDown={(event) => {
                event.preventDefault();
                if (blockType === 'bullet') {
                  // TODO: this will remove the top level list, so for nested lists, we need to set the nested list to become paragraph
                  editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                } else {
                  editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                }
              }}
            />
          </FloatingMenuTooltip>
          <FloatingMenuTooltip title="Numbering">
            <IconButton
              tabIndex={-1}
              className={styles.toolbarButton({ active: blockType === 'number' })}
              label="Ordered list"
              icon={Icons.OrderedList}
              variant="text"
              onMouseDown={(event) => {
                event.preventDefault();
                if (blockType === 'number') {
                  // TODO: this will remove the top level list, so for nested lists, we need to set the nested list to become paragraph
                  editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                } else {
                  editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                }
              }}
            />
          </FloatingMenuTooltip>
          <FloatingMenuTooltip title="Add link">
            <FloatingLinkEditorPlugin editor={editor} active={blockType === 'link'} />
          </FloatingMenuTooltip>
        </>
      )}

      {options?.enableTextAlign && (
        <>
          <Divider style={{ minHeight: 24 }} />
          <FloatingMenuTooltip title="Align left">
            <TextAlignButton editor={editor} align="left" />
          </FloatingMenuTooltip>
          <FloatingMenuTooltip title="Align center">
            <TextAlignButton editor={editor} align="center" />
          </FloatingMenuTooltip>
          <FloatingMenuTooltip title="Align right">
            <TextAlignButton editor={editor} align="right" />
          </FloatingMenuTooltip>
        </>
      )}
    </Flex.Container>
  );
}

// export function FloatingMenuPlugin({ element }: Parameters<typeof FloatingMenuPluginPrimitive>[0]) {
//   return <FloatingMenuPluginPrimitive element={element} MenuComponent={FloatingMenu} />;
// }

// export default FloatingMenuPlugin;

function highlightText(editor: LexicalEditor, color: string) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // If we just have the cursor and no selection, do not doing anything.
      const { anchor, focus } = selection;
      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      // Use $patchStyleText as a hack to split nodes properly for us,
      // then use the updated selection to update the proper nodes.
      $patchStyleText(selection, {
        '--mutiny-set-highlight': color,
      });
      const newSelection = $getSelection();
      if (newSelection) {
        parseNodesFromSelection(newSelection).forEach((node) => {
          if ($isExtendedTextNode(node)) {
            node.setHighlightColor(color);
            // Set resolved color immediately so it renders in both edit and display modes
            node.setResolvedHighlightColor(color);
          }
        });
        newSelection.dirty = true;
      }
    }
  });
}

function colorText(editor: LexicalEditor, color: string) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // If we just have the cursor and no selection, do not doing anything.
      const { anchor, focus } = selection;
      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      // Use $patchStyleText as a hack to split nodes properly for us,
      // then use the updated selection to update the proper nodes.
      $patchStyleText(selection, {
        '--mutiny-set-font-color': color,
      });
      const newSelection = $getSelection();
      if (newSelection) {
        parseNodesFromSelection(newSelection).forEach((node) => {
          if ($isExtendedTextNode(node)) {
            node.setFontColor(color);
            // Set resolved color immediately so it renders in both edit and display modes
            node.setResolvedFontColor(color);
          }
        });
        newSelection.dirty = true;
      }
    }
  });
}

function removeFormatting(editor: LexicalEditor) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // If we just have the cursor and no selection, do not doing anything.
      const { anchor, focus } = selection;
      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      // We need to use $patchStyleText with a dummy style as a hack to properly split the nodes
      $patchStyleText(selection, {
        '--mutiny-remove-formatting': 'true',
      });

      parseNodesFromSelection(selection).forEach((node) => {
        if ($isExtendedTextNode(node)) {
          if (node.getFormat() !== 0) {
            node.setFormat(0);
          }
          node.setHighlightColor('');
          node.setFontColor('');
          node.setResolvedHighlightColor('');
          node.setResolvedFontColor('');
        }
      });
      selection.format = 0;
    }
  });
}
